import { red } from '@ant-design/colors';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Space } from '@client/components/_ui/layout/Space';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { RiskAdd } from '@client/components/risks/RiskAdd';
import { useRiskFilterConfig } from '@client/components/risks/useRiskFilterConfig';
import { TableViewsBar, useTableViews } from '@client/components/table-views';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import type { Action } from '@client/types/common';
import { htmlToText } from '@client/utils';
import { formatRiskNumber } from '@client/utils/domain/risk';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { capitalize } from '@shared/utils/core/string';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Tooltip, Typography } from 'antd';
import { Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const RiskSearchSchema = z.object({
    assessmentRowIndex: z.coerce.number().optional(),
    assessmentColIndex: z.coerce.number().optional(),
    assessmentType: z.enum(['inherent', 'residual']).optional(),
    'risk-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.coerce.bigint(),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/risk/register/')({
    staticData: {
        ui: {
            title: 'Risk register',
            subtitle: 'Manage risks and their associated assessments',
        },
    },
    validateSearch: zodValidator(RiskSearchSchema),
    component: RiskRegisterSearch,
});

type QueryParams = RouterInputs['risk']['listRisks'];
type RiskRow = NonNullable<RouterOutputs['risk']['listRisks']>['rows'][number];
type RiskOrderBy = NonNullable<QueryParams['orderBy']>;

function useRiskTableColumns(
    orgId: bigint,
    onDelete: (id: bigint, onSuccess: () => void) => void,
    isDeleting: boolean,
): readonly PrismaTableColumn<RiskRow, RiskOrderBy, any>[] {
    const columnHelper = createColumnHelper<RiskRow, RiskOrderBy>();
    const { formatDate } = useFormatter();

    return [
        columnHelper.display({
            id: 'warning',
            header: '',
            cell: ({ row: record }) =>
                !!record.riskAssessment?.isResidualRiskOutsideAppetite && (
                    <Tooltip title="Residual risk is outside of risk appetite">
                        <ExclamationCircleOutlined style={{ color: red.primary }} className="align-middle text-xl" />
                    </Tooltip>
                ),
            width: 25,
        }),
        columnHelper.accessor('id', {
            id: 'riskNo',
            header: 'Risk No.',
            sortable: 'id',
            cell: ({ value, row: record }) => {
                const isOutOfSync = record.isMatrixOutOfSync;

                return (
                    <>
                        {formatRiskNumber(value, record.LevelOneCategory?.riskNumberPrefix)}
                        {isOutOfSync && (
                            <Tooltip title="Risk matrix is out of sync with current configuration">
                                (Out of sync){' '}
                            </Tooltip>
                        )}
                    </>
                );
            },
            fixed: 'left',
        }),
        columnHelper.accessor((row) => row.LevelOneCategory?.name, {
            id: 'category',
            header: 'Category',
            sortable: 'LevelOneRiskCategory.name',
            cell: ({ row }) => row.LevelOneCategory?.name || '',
        }),
        columnHelper.accessor('title', {
            id: 'title',
            header: 'Title',
            sortable: 'title',
            cell: ({ row }) => (
                <Tooltip title={row.title}>
                    <Typography.Paragraph ellipsis style={{ maxWidth: 500, minWidth: 100, marginBottom: 0 }}>
                        {row.title}
                    </Typography.Paragraph>
                </Tooltip>
            ),
        }),
        columnHelper.accessor('riskAssessment', {
            id: 'impact',
            header: 'Impact',
            cell: ({ row }) => row.riskAssessment?.impacts?.[row.riskAssessment?.selectedResidualCell?.rowIndex] || '',
        }),
        columnHelper.accessor('description', {
            id: 'description',
            header: 'Description',
            sortable: 'description',
            cell: ({ row }) => <ComponentPreview>{htmlToText(row.description)}</ComponentPreview>,
        }),
        columnHelper.display({
            id: 'owner',
            header: 'Owner',
            cell: ({ row }) => (
                <User userId={row.Owner?.userId} name={row.Owner?.fullName} photoUrl={row.Owner?.User?.photoUrl} />
            ),
        }),
        columnHelper.accessor('dateIdentified', {
            id: 'dateIdentified',
            header: 'Date identified',
            sortable: 'dateIdentified',
            cell: ({ row }) => formatDate(row.dateIdentified),
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Space size="small">
                    <Button.OpenInNewTab
                        to="/console/$orgId/risk/register/details/$id/assessment"
                        params={{
                            orgId: orgId,
                            id: row.id,
                        }}
                        tooltip="Open in new tab"
                    />
                    <Button.Delete disabled={isDeleting} onConfirm={(onSuccess) => onDelete(row.id, onSuccess)} />
                </Space>
            ),
        }),
    ] as const;
}

function RiskRegisterSearch() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();

    const [action, setAction] = useState<Action>(['none']);
    const { config: filterConfig, ready: filterConfigReady } = useRiskFilterConfig();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        where: buildInitialWhereFromFilters(filterConfig, searchParams['risk-filters']),
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isPending, refetch, isRefetching } = trpc.risk.listRisks.useQuery(input, {
        enabled: filterConfigReady && viewsReady,
        refetchInterval: 1000 * 5,
    });
    const deletionInFlight = useRef(false);
    const deleteRiskMutation = trpc.risk.deleteRisk.useMutation({
        onError: (error) => errorMessage.show(error),
        onSettled: () => {
            deletionInFlight.current = false;
        },
    });

    const {
        assessmentRowIndex: rawAssessmentRowIndex,
        assessmentColIndex: rawAssessmentColIndex,
        assessmentType,
    } = searchParams;

    useEffect(() => {
        const assessmentRowIndex = Number(rawAssessmentRowIndex);
        const assessmentColIndex = Number(rawAssessmentColIndex);

        if (
            Number.isNaN(assessmentRowIndex) ||
            Number.isNaN(assessmentColIndex) ||
            (assessmentType !== 'inherent' && assessmentType !== 'residual')
        ) {
            return;
        }

        setTimeout(() => {
            setInput((prev) => ({
                ...prev,
                where: {
                    ...prev.where,
                    riskAssessment: {
                        path: ['selected' + capitalize(assessmentType) + 'Cell'],
                        equals: {
                            rowIndex: assessmentRowIndex,
                            colIndex: assessmentColIndex,
                        },
                    },
                },
            }));
        }, 0);
    }, [rawAssessmentRowIndex, rawAssessmentColIndex, assessmentType, setInput]);

    useEffect(() => {
        if (isRefetching) {
            console.log('refetching');
            console.log(data);
        }
    }, [isRefetching]);

    const onDelete = (id: bigint, onSuccess: () => void) => {
        // Confirmations can already be open before React disables the row triggers.
        // Keep the observer on this invocation until settlement, including retries.
        if (deletionInFlight.current) return;
        deletionInFlight.current = true;
        deleteRiskMutation.mutate(
            { id },
            {
                onSuccess: () => {
                    onSuccess();
                    void refetch();
                },
            },
        );
    };

    const columns = useRiskTableColumns(orgId, onDelete, deleteRiskMutation.isPending);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isPending || deleteRiskMutation.isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        emptyState: { noRecordsTitle: 'No risks yet' },
    });

    const views = useTableViews({
        entityType: 'RISK',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'risk-filters',
        initialFilters: searchParams['risk-filters'],
        initialActiveViewId: searchParams.view,
        queryParams: input,
        setQueryParams: setInput,
        table: tableProps,
        // Risk register has no column-visibility persistence → skip column capture/apply.
        columnPersistKey: undefined,
        defaultOrderBy: { id: 'desc' },
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <RiskAdd
                action={action}
                onClose={() => {
                    setAction(['none']);
                    void refetch();
                }}
            />

            <TableViewsBar controller={views.controller} />

            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    <Button
                        type="primary"
                        icon={<Plus />}
                        onClick={() => {
                            setAction(['add']);
                        }}
                    >
                        Add risk
                    </Button>
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick: (e) =>
                        navigate(
                            {
                                to: '/console/$orgId/risk/register/details/$id/assessment',
                                params: {
                                    orgId,
                                    id,
                                },
                            },
                            e,
                        ),
                })}
            />
        </PageController>
    );
}
